import {
  Button,
  Grid,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  TableBody,
} from '@material-ui/core';
import {
  CheckCircleOutline,
  InfoRounded,
  Keyboard,
  Link as LinkIcon,
} from '@material-ui/icons';
import QrCodeIcon from '@material-ui/icons/CenterFocusStrong';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import FilterNoneOutlinedIcon from '@material-ui/icons/FilterNoneOutlined';
import { useSnackbar } from 'notistack';
import React from 'react';
import ReactDOM from 'react-dom';
import QRCode from 'qrcode.react';
import { noDataAvailable } from '../../../components/NoDataAvailable';
import { useAppContext } from '../../../config/AppContext';
import { IVenueLocation } from '../../../models/Location.model';
import { getDeliveryLocationWebLink } from '../../../services/WebLinks.service';
import FanfoodLogo from '../../../images/logo_red.png';
import './VenueLocation.scss';

interface IVenueLocationTable {
  locations: IVenueLocation[];
  handleLocationSelection: (location: IVenueLocation) => void;
  onCloneLocation: (location: IVenueLocation) => void;
  onDeleteLocation: (location: IVenueLocation) => void;
  onEditLocation: (location: IVenueLocation) => void;
}

export const VenueLocationTable = ({
  locations,
  handleLocationSelection,
  onEditLocation,
  onDeleteLocation,
  onCloneLocation,
}: IVenueLocationTable) => {
  const { enqueueSnackbar } = useSnackbar();
  const { venue } = useAppContext();
  const [downloadingLocationId, setDownloadingLocationId] = React.useState<
    string | null
  >(null);

  if (!venue) {
    return null;
  }

  const copyLocationLinkToClipboard = (deliveryLocationId: string) => {
    navigator.clipboard.writeText(
      getDeliveryLocationWebLink(venue.id, deliveryLocationId),
    );
    enqueueSnackbar('Venue Delivery Link Copied to Clipboard', {
      variant: 'success',
      autoHideDuration: 8000,
    });
  };

  const handleDownloadQr = (locationId: string) => {
    setDownloadingLocationId(locationId);

    // Find the location object to get the name
    const location = locations.find(loc => loc.id === locationId);
    if (!location) {
      enqueueSnackbar('Location not found', {
        variant: 'error',
        autoHideDuration: 8000,
      });
      setDownloadingLocationId(null);
      return;
    }

    // Create a temporary container for the QR code
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.top = '-9999px';
    document.body.appendChild(container);

    // Render the QR code using qrcode.react
    const qrValue = getDeliveryLocationWebLink(venue.id, locationId);
    ReactDOM.render(
      <QRCode value={qrValue} size={256} level="H" renderAs="svg" />,
      container,
      () => {
        // Get the SVG element after rendering
        const svg = container.querySelector('svg');
        if (!svg) {
          enqueueSnackbar('Failed to generate QR code', {
            variant: 'error',
            autoHideDuration: 8000,
          });
          document.body.removeChild(container);
          setDownloadingLocationId(null);
          return;
        }

        // Serialize SVG to string
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        const padding = 20;
        canvas.width = 256 + padding * 2;
        canvas.height = 256 + padding * 2;

        img.onload = () => {
          if (!ctx) {
            enqueueSnackbar('Failed to generate QR code', {
              variant: 'error',
              autoHideDuration: 8000,
            });
            document.body.removeChild(container);
            setDownloadingLocationId(null);
            return;
          }

          // Draw background
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Draw QR code
          ctx.drawImage(img, padding, padding, 256, 256);

          // Add logo overlay
          const logo = new Image();
          logo.src = FanfoodLogo;
          logo.onload = () => {
            const logoSize = 256 * 0.2; // 20% of QR code size
            const logoX = (canvas.width - logoSize) / 2;
            const logoY = (canvas.height - logoSize) / 2;
            ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);

            const pngUrl = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.href = pngUrl;
            downloadLink.download = `${location.name}.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            document.body.removeChild(container);
            setDownloadingLocationId(null);
          };

          logo.onerror = () => {
            // Fallback if logo fails to load
            const pngUrl = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.href = pngUrl;
            downloadLink.download = `${location.name}.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            document.body.removeChild(container);
            setDownloadingLocationId(null);
          };
        };

        img.onerror = () => {
          enqueueSnackbar('Failed to generate QR code', {
            variant: 'error',
            autoHideDuration: 8000,
          });
          document.body.removeChild(container);
          setDownloadingLocationId(null);
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
      },
    );
  };

  return (
    <div className="locations-table-container">
      <Table>
        <TableHead>
          <TableRow
            classes={{
              root: 'table-row',
            }}
          >
            <TableCell classes={{ root: 'table-cell table-cell-head' }}>
              Name
            </TableCell>
            <TableCell classes={{ root: 'table-cell table-cell-head' }}>
              Type
            </TableCell>
            <TableCell classes={{ root: 'table-cell table-cell-head' }}>
              <Tooltip
                title={`Will show up as the dropdown label for the next section in our apps. E.g. a location "Section 1" may have a Nested Label: "Row"`}
                placement="top"
              >
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexWrap: 'wrap',
                  }}
                >
                  <span>Nested Label</span>
                  <InfoRounded />
                </div>
              </Tooltip>
            </TableCell>
            <TableCell classes={{ root: 'table-cell-action table-cell-head' }}>
              Action
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {locations && locations.length
            ? locations.map((location: IVenueLocation) => (
                <TableRow
                  key={location.id}
                  id={location.id}
                  className="locations-table-row"
                >
                  <TableCell
                    onClick={() => handleLocationSelection(location)}
                    className="table-cell table-cell-location-name"
                  >
                    <Grid container={true} direction="row" alignItems="center">
                      {location.name}{' '}
                      {location.isDeliveryLocation && (
                        <Tooltip
                          title="This location can be selected for delivery"
                          placement="top"
                        >
                          <CheckCircleOutline color="primary" />
                        </Tooltip>
                      )}
                      {location.isDeliveryLocation && location.isCustomInput && (
                        <Tooltip
                          title="This location is a Custom Input Field"
                          placement="top"
                        >
                          <Keyboard color="secondary" />
                        </Tooltip>
                      )}
                    </Grid>
                  </TableCell>
                  <TableCell className="table-cell">{location.type}</TableCell>
                  <TableCell className="table-cell">
                    {location.categoryName}
                  </TableCell>
                  <TableCell classes={{ root: 'table-cell-action' }}>
                    <Button
                      size="small"
                      onClick={() => {
                        onEditLocation(location);
                      }}
                      classes={{
                        root: 'locations-table-button',
                      }}
                      value="edit"
                    >
                      <div className="icon-title-wrapper">
                        <EditOutlinedIcon />
                        <span className="icon-title">Edit</span>
                      </div>
                    </Button>
                    <Button
                      size="small"
                      classes={{ root: 'locations-table-button' }}
                      onClick={() => onCloneLocation(location)}
                    >
                      <div className="icon-title-wrapper">
                        <FilterNoneOutlinedIcon />
                        <span className="icon-title">Clone</span>
                      </div>
                    </Button>
                    <Button
                      size="small"
                      value="delete"
                      onClick={() => {
                        onDeleteLocation(location);
                      }}
                      classes={{
                        root: 'locations-table-button',
                      }}
                    >
                      <div className="icon-title-wrapper">
                        <DeleteOutlinedIcon />
                        <span className="icon-title">Delete</span>
                      </div>
                    </Button>
                    <Button
                      size="small"
                      onClick={() => copyLocationLinkToClipboard(location.id)}
                      classes={{
                        root: 'locations-table-button',
                      }}
                    >
                      <div className="icon-title-wrapper">
                        <LinkIcon />
                        <span className="icon-title">Copy Link</span>
                      </div>
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleDownloadQr(location.id)}
                      classes={{
                        root: 'locations-table-button',
                      }}
                      disabled={downloadingLocationId === location.id}
                      aria-label={`Download QR code for ${location.name}`}
                    >
                      <div className="icon-title-wrapper">
                        {downloadingLocationId === location.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <QrCodeIcon />
                        )}
                        <span className="icon-title">QR Code</span>
                      </div>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            : noDataAvailable(4)}
        </TableBody>
      </Table>
    </div>
  );
};
